import { useState, useEffect } from 'react';
import api from '../../services/api';
import Modal from '../UI/Modal';

const PATHOLOGIE_ICONS = {
    nutrition: '🟢',
    perfusion: '🔵',
    parkinson: '⚫',
};

export default function RecurrenceManager() {
    const [recurrences, setRecurrences] = useState([]);
    const [patients, setPatients] = useState([]);
    const [users, setUsers] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [selected, setSelected] = useState(null);
    const [loading, setLoading] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null);
    // { type: 'delete'|'regenerate', rec: recurrence }
    const [form, setForm] = useState({
        patient: '',
        livreur: '',
        jourSemaine: 'lundi',
        periode: 'matin',
        consigneHoraire: '',
        frequence: '1',
        dateDebut: '',
        dateFin: '',
        actif: true,
    });
    const [search, setSearch] = useState('');
    const [patientSearch, setPatientSearch] = useState('');
    const [showPatientDropdown, setShowPatientDropdown] = useState(false);
    const filteredRecurrences = recurrences
        .filter(r =>
            r.patient?.nom?.toLowerCase().includes(search.toLowerCase()) ||
            r.patient?.ville?.toLowerCase().includes(search.toLowerCase()) ||
            r.livreur?.nom?.toLowerCase().includes(search.toLowerCase())
        )
        .sort((a, b) => (a.patient?.nom || '').localeCompare(b.patient?.nom || ''));

    const [exceptionsModal, setExceptionsModal] = useState(null); // recurrence concernée
    const [exceptions, setExceptions] = useState([]);
    const [loadingExceptions, setLoadingExceptions] = useState(false);

    useEffect(() => { loadAll(); }, []);

    const loadAll = async () => {
        try {
            const [recRes, patRes, userRes] = await Promise.all([
                api.get('/recurrences'),
                api.get('/patients'),
                api.get('/users'),
            ]);
            setRecurrences(recRes.data.member || []);
            setPatients(patRes.data.member || []);
            setUsers(userRes.data.member || []);
        } catch (err) {
            console.error(err);
        }
    };

    const openEdit = (rec) => {
        setSelected(rec);
        setPatientSearch(rec.patient ? `${PATHOLOGIE_ICONS[rec.patient.pathologie] || '⚪'} ${rec.patient.nom} — ${rec.patient.ville}` : '');
        setForm({
            patient: rec.patient?.['@id'] || '',
            livreur: rec.livreur?.['@id'] || '',
            jourSemaine: rec.jourSemaine || 'lundi',
            periode: rec.periode || 'matin',
            consigneHoraire: rec.consigneHoraire || '',
            frequence: String(rec.frequence || 1),
            dateDebut: rec.dateDebut?.substring(0, 10) || '',
            dateFin: rec.dateFin?.substring(0, 10) || '',
            actif: rec.actif ?? true,
            imuno: rec.imuno ?? false,
        });
        setShowModal(true);
    };

    const openCreate = () => {
        setSelected(null);
        setForm({
            patient: '', livreur: '', jourSemaine: 'lundi', periode: 'matin',
            consigneHoraire: '', frequence: '1', dateDebut: '', dateFin: '', actif: true, imuno: false,
        });
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        const payload = {
            patient: form.patient,
            livreur: form.livreur,
            jourSemaine: form.jourSemaine,
            periode: form.periode,
            consigneHoraire: form.consigneHoraire || null,
            frequence: form.frequence === '30j' ? 99 : parseInt(form.frequence),
            dateDebut: form.dateDebut,
            dateFin: form.dateFin || null,
            actif: form.actif,
            imuno: form.imuno,
        };
        try {
            if (selected) {
                await api.patch(`/recurrences/${selected.id}`, payload, {
                    headers: { 'Content-Type': 'application/merge-patch+json' }
                });
            } else {
                await api.post('/recurrences', payload, {
                    headers: { 'Content-Type': 'application/ld+json' }
                });
            }
            setShowModal(false);
            loadAll();
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const openExceptions = async (rec) => {
        setExceptionsModal(rec);
        setLoadingExceptions(true);
        try {
            const res = await api.get(`/recurrence_exceptions?recurrence=/api/recurrences/${rec.id}`);
            setExceptions(res.data.member || []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoadingExceptions(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            await api.delete(`/recurrences/${id}`);
            setConfirmAction(null);
            loadAll();
        } catch (err) {
            console.error(err);
        }
    };

    const toggleActif = async (rec) => {
        try {
            await api.patch(`/recurrences/${rec.id}`, { actif: !rec.actif }, {
                headers: { 'Content-Type': 'application/merge-patch+json' }
            });
            loadAll();
        } catch (err) {
            console.error(err);
        }
    };

    const handleRegenerate = async (rec) => {
        try {
            await api.post(`/recurrences/${rec.id}/regenerate`, {}, {
                headers: { 'Content-Type': 'application/ld+json' }
            });
            setConfirmAction(null);
            loadAll();
        } catch (err) {
            console.error(err);
        }
    };

    const frequenceLabel = {
        '1': 'Chaque semaine',
        '2': 'Toutes les 2 semaines',
        '4': 'Toutes les 4 semaines',
        '99': 'Tous les 30 jours',
    };

    return (
        <div>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px'}}>
                <h3 style={{margin: 0}}>Récurrences ({recurrences.length})</h3>
                <button onClick={openCreate} style={addButtonStyle}>+ Ajouter</button>
            </div>

            <div style={{marginBottom: '16px'}}>
                <input
                    type="text"
                    placeholder="🔍 Rechercher par patient, ville ou livreur..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    style={{
                        width: '100%',
                        padding: '10px 14px',
                        borderRadius: '8px',
                        border: '1px solid #e0e0e0',
                        fontSize: '14px',
                        boxSizing: 'border-box',
                        backgroundColor: 'white',
                    }}
                />
            </div>

            <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                overflow: 'hidden',
                boxShadow: '0 1px 6px rgba(0,0,0,0.07)'
            }}>
                <table style={{width: '100%', borderCollapse: 'collapse'}}>
                    <thead>
                    <tr style={{backgroundColor: '#f8fafc'}}>
                        <th style={thStyle}>ID</th>
                        <th style={thStyle}>Patient</th>
                        <th style={thStyle}>Livreur</th>
                        <th style={thStyle}>Jour</th>
                        <th style={thStyle}>Période</th>
                        <th style={thStyle}>Consigne</th>
                        <th style={thStyle}>Fréquence</th>
                        <th style={thStyle}>Actif</th>
                        <th style={thStyle}>Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    {filteredRecurrences.map(rec => (
                        <tr key={rec.id} style={{borderTop: '1px solid #f0f0f0', opacity: rec.actif ? 1 : 0.5}}>
                            <td style={tdStyle}><span
                                style={{fontSize: '12px', fontWeight: '700', color: '#a78bfa'}}>R#{rec.id}</span>
                            </td>
                            <td style={tdStyle}>{rec.patient?.nom} <span
                                style={{color: '#888', fontSize: '12px'}}>({rec.patient?.ville})</span></td>
                            <td style={tdStyle}>{rec.livreur?.nom}</td>
                            <td style={tdStyle}>
                                <div style={{display: 'flex', alignItems: 'center', gap: '4px'}}>
                                    {rec.periode === 'matin'
                                        ? <><i className="ti ti-sun"/><span>Matin</span></>
                                        : <><i className="ti ti-moon"/><span>Après-midi</span></>
                                    }
                                </div>
                            </td>
                            <td style={tdStyle}>{rec.consigneHoraire || '—'}</td>
                            <td style={tdStyle}>{frequenceLabel[String(rec.frequence)]}</td>
                            <td style={tdStyle}>
                                <button
                                    onClick={() => toggleActif(rec)}
                                    style={{
                                        padding: '3px 10px',
                                        borderRadius: '20px',
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        border: 'none',
                                        cursor: 'pointer',
                                        backgroundColor: rec.actif ? '#dcfce7' : '#fee2e2',
                                        color: rec.actif ? '#16a34a' : '#dc2626',
                                    }}
                                >
                                    {rec.actif ? 'Actif' : 'Inactif'}
                                </button>
                            </td>
                            <td style={tdStyle}>
                                <div style={{display: 'flex', gap: '8px'}}>
                                    <button onClick={() => openEdit(rec)} style={editButtonStyle} title="Modifier"><i
                                        className="ti ti-pencil"/></button>
                                    <button onClick={() => setConfirmAction({type: 'regenerate', rec})}
                                            style={regenerateButtonStyle} title="Régénérer"><i
                                        className="ti ti-refresh"/></button>
                                    <button onClick={() => openExceptions(rec)} style={{
                                        padding: '4px 8px',
                                        backgroundColor: '#fef3c7',
                                        border: 'none',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                    }} title="Voir les exceptions">
                                        <i className="ti ti-calendar-off"/>
                                    </button>
                                    <button onClick={() => setConfirmAction({type: 'delete', rec})}
                                            style={deleteButtonStyle} title="Supprimer"><i className="ti ti-trash"/>
                                    </button>
                                </div>
                            </td>
                        </tr>
                    ))}
                    {recurrences.length === 0 && (
                        <tr>
                            <td colSpan={8} style={{...tdStyle, textAlign: 'center', color: '#888', padding: '32px'}}>
                                Aucune récurrence
                            </td>
                        </tr>
                    )}
                    </tbody>
                </table>
            </div>

            {exceptionsModal && (
                <Modal title={`Exceptions — R#${exceptionsModal.id} ${exceptionsModal.patient?.nom}`} onClose={() => setExceptionsModal(null)}>
                    {loadingExceptions ? (
                        <div style={{ textAlign: 'center', padding: '20px', color: '#888' }}>Chargement...</div>
                    ) : exceptions.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '20px', color: '#888' }}>
                            <i className="ti ti-check" style={{ marginRight: '6px' }} />
                            Aucune exception active
                        </div>
                    ) : (
                        <div>
                            <p style={{ fontSize: '13px', color: '#888', marginBottom: '16px' }}>
                                Ces dates ne génèreront pas de livraison. Elles seront supprimées automatiquement par le cron.
                            </p>
                            {exceptions.map(e => (
                                <div key={e.id} style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    padding: '8px 12px',
                                    borderRadius: '8px',
                                    backgroundColor: '#fef3c7',
                                    marginBottom: '6px',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    color: '#d97706',
                                }}>
                                    <i className="ti ti-calendar-off" />
                                    {new Date(e.dateExclue).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                                </div>
                            ))}
                        </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
                        <button onClick={() => setExceptionsModal(null)} style={cancelButtonStyle}>Fermer</button>
                    </div>
                </Modal>
            )}

            {confirmAction && (
                <Modal
                    title={confirmAction.type === 'delete' ? 'Supprimer la récurrence' : 'Régénérer les livraisons'}
                    onClose={() => setConfirmAction(null)}
                >
                    <p style={{marginBottom: '24px', color: '#444'}}>
                        {confirmAction.type === 'delete'
                            ? 'Supprimer cette récurrence ? Les livraisons déjà générées ne seront pas supprimées.'
                            : 'Cela va mettre à jour les livraisons futures existantes (livreur, période, consigne) sans perdre les notes et statuts déjà saisis.'}
                    </p>
                    <div style={{display: 'flex', gap: '12px', justifyContent: 'flex-end'}}>
                        <button onClick={() => setConfirmAction(null)} style={cancelButtonStyle}>
                            Annuler
                        </button>
                        <button
                            onClick={() => confirmAction.type === 'delete'
                                ? handleDelete(confirmAction.rec.id)
                                : handleRegenerate(confirmAction.rec)
                            }
                            style={{
                                ...submitButtonStyle,
                                backgroundColor: confirmAction.type === 'delete' ? '#ef4444' : '#4f46e5',
                            }}
                        >
                            {confirmAction.type === 'delete' ? 'Supprimer' : 'Régénérer'}
                        </button>
                    </div>
                </Modal>
            )}

            {showModal && (
                <Modal title={selected ? 'Modifier la récurrence' : 'Nouvelle récurrence'}
                       onClose={() => setShowModal(false)}>
                    <form onSubmit={handleSubmit}>
                        <div style={fieldStyle}>
                            <label style={labelStyle}>Patient *</label>
                            <div style={{position: 'relative'}}>
                                <input
                                    type="text"
                                    placeholder="Rechercher un patient..."
                                    value={patientSearch}
                                    onChange={e => {
                                        setPatientSearch(e.target.value);
                                        setShowPatientDropdown(true);
                                        setForm({...form, patient: ''});
                                    }}
                                    onFocus={() => setShowPatientDropdown(true)}
                                    style={inputStyle}
                                    required={!form.patient}
                                />
                                {/* Champ caché pour valider que le patient est bien sélectionné */}
                                {form.patient && <input type="hidden" value={form.patient} required/>}
                                {showPatientDropdown && patientSearch.length >= 1 && (
                                    <div style={{
                                        position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
                                        backgroundColor: 'white', border: '1px solid #ddd', borderRadius: '8px',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: '200px', overflowY: 'auto',
                                    }}>
                                        {patients
                                            .filter(p => p.nom?.toLowerCase().includes(patientSearch.toLowerCase()) || p.ville?.toLowerCase().includes(patientSearch.toLowerCase()))
                                            .slice(0, 10)
                                            .map(p => (
                                                <div
                                                    key={p['@id']}
                                                    onClick={() => {
                                                        setForm({...form, patient: p['@id']});
                                                        setPatientSearch(`${PATHOLOGIE_ICONS[p.pathologie] || '⚪'} ${p.nom} — ${p.ville}`);
                                                        setShowPatientDropdown(false);
                                                    }}
                                                    style={{
                                                        padding: '8px 12px', cursor: 'pointer', fontSize: '13px',
                                                        borderBottom: '1px solid #f0f0f0',
                                                    }}
                                                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f5f3ff'}
                                                    onMouseLeave={e => e.currentTarget.style.backgroundColor = 'white'}
                                                >
                                                    {PATHOLOGIE_ICONS[p.pathologie] || '⚪'} {p.nom} — {p.ville}
                                                </div>
                                            ))
                                        }
                                        {patients.filter(p => p.nom?.toLowerCase().includes(patientSearch.toLowerCase())).length === 0 && (
                                            <div style={{padding: '8px 12px', color: '#888', fontSize: '13px'}}>Aucun
                                                résultat</div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div style={fieldStyle}>
                            <label style={labelStyle}>Livreur *</label>
                            <select value={form.livreur} onChange={e => setForm({...form, livreur: e.target.value})}
                                    style={inputStyle} required>
                                <option value="">Sélectionner</option>
                                {users.map(u => <option key={u['@id']} value={u['@id']}>{u.nom || u.email}</option>)}
                            </select>
                        </div>
                        <div style={{display: 'flex', gap: '12px'}}>
                            <div style={{...fieldStyle, flex: 1}}>
                                <label style={labelStyle}>Jour *</label>
                                <select value={form.jourSemaine}
                                        onChange={e => setForm({...form, jourSemaine: e.target.value})}
                                        style={inputStyle}>
                                    {['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi'].map(j => <option key={j}
                                                                                                          value={j}>{j.charAt(0).toUpperCase() + j.slice(1)}</option>)}
                                </select>
                            </div>
                            <div style={{...fieldStyle, flex: 1}}>
                                <label style={labelStyle}>Période *</label>
                                <select value={form.periode} onChange={e => setForm({...form, periode: e.target.value})}
                                        style={inputStyle}>
                                    <option value="matin">Matin</option>
                                    <option value="aprem">Après-midi</option>
                                </select>
                            </div>
                        </div>
                        <div style={fieldStyle}>
                            <label style={labelStyle}>⏰ Consigne horaire (optionnel)</label>
                            <input
                                type="text"
                                value={form.consigneHoraire}
                                onChange={e => setForm({...form, consigneHoraire: e.target.value})}
                                style={inputStyle}
                                placeholder="Ex: avant 10h, entre 14h et 15h..."
                            />
                        </div>
                        <div style={fieldStyle}>
                            <label style={labelStyle}>Fréquence *</label>
                            <select value={form.frequence} onChange={e => setForm({...form, frequence: e.target.value})}
                                    style={inputStyle}>
                                <option value="1">Chaque semaine</option>
                                <option value="2">Toutes les 2 semaines</option>
                                <option value="4">Toutes les 4 semaines</option>
                                <option value="99">Tous les 30 jours</option>
                            </select>
                        </div>
                        <div style={{display: 'flex', gap: '12px'}}>
                            <div style={{...fieldStyle, flex: 1}}>
                                <label style={labelStyle}>Date début *</label>
                                <input type="date" value={form.dateDebut}
                                       onChange={e => setForm({...form, dateDebut: e.target.value})} style={inputStyle}
                                       required/>
                            </div>
                            <div style={{...fieldStyle, flex: 1}}>
                                <label style={labelStyle}>Date fin</label>
                                <input type="date" value={form.dateFin}
                                       onChange={e => setForm({...form, dateFin: e.target.value})} style={inputStyle}/>
                            </div>
                        </div>
                        <div style={{...fieldStyle}}>
                            <label style={labelStyle}><i className="ti ti-star" style={{marginRight: '4px'}}/>Immunosuppresseur</label>
                            <button
                                type="button"
                                onClick={() => setForm({...form, imuno: !form.imuno})}
                                style={{
                                    padding: '9px 16px',
                                    borderRadius: '8px',
                                    border: '1.5px solid #f59e0b',
                                    backgroundColor: form.imuno ? '#fef3c7' : 'white',
                                    color: form.imuno ? '#d97706' : '#888',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                }}
                            >
                                {form.imuno ? <><i className="ti ti-star" style={{marginRight: '4px'}}/>Immuno
                                    activé</> : <><i className="ti ti-star-off" style={{marginRight: '4px'}}/>Immuno
                                    désactivé</>}
                            </button>
                        </div>
                        <div style={{display: 'flex', gap: '12px', justifyContent: 'flex-end'}}>
                            <button type="button" onClick={() => setShowModal(false)}
                                    style={cancelButtonStyle}>Annuler
                            </button>
                            <button type="submit" style={submitButtonStyle} disabled={loading}>
                                {loading ? 'Enregistrement...' : selected ? 'Modifier' : 'Créer'}
                            </button>
                        </div>
                    </form>
                </Modal>
            )}
        </div>
    );
}

const addButtonStyle = {
    padding: '8px 16px',
    backgroundColor: '#4f46e5',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600'
};
const editButtonStyle = {
    padding: '4px 8px',
    backgroundColor: '#f0f2f5',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer'
};
const deleteButtonStyle = {
    padding: '4px 8px',
    backgroundColor: '#fee2e2',
    border: 'none',
    borderRadius: '6px', cursor: 'pointer'
};
const thStyle = {padding: '12px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#888'};
const tdStyle = {padding: '12px 16px', fontSize: '14px', color: 'var(--text-muted)', verticalAlign: 'middle'};
const fieldStyle = {marginBottom: '16px'};
const labelStyle = {display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#444'};
const inputStyle = {
    width: '100%',
    padding: '9px 12px',
    borderRadius: '8px',
    border: '1px solid #ddd',
    fontSize: '14px',
    boxSizing: 'border-box'
};
const cancelButtonStyle = {
    padding: '10px 20px',
    backgroundColor: '#f0f2f5',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px'
};
const submitButtonStyle = {
    padding: '10px 20px',
    backgroundColor: '#4f46e5',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600'
};
const regenerateButtonStyle = {
    padding: '4px 8px',
    backgroundColor: '#dbeafe',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer'
};